import type { ReactNode } from "react";

/**
 * De kop die elk catalogusscherm deelt: één rij grote tegels om op soort te kiezen, en rechtsboven de
 * lijst dezelfde drie weergaven als het dieroverzicht.
 *
 * Producten, asieltarieven, pensiontarieven, crèchetarieven, ambulancetarieven en crematietarieven
 * zijn allemaal "een lijst met een soort-indeling en een prijs". Ze hadden alle zes hun eigen kop met
 * eigen filters op eigen plekken; wie van het ene scherm naar het andere ging moest opnieuw zoeken
 * waar de keuze zat. Dit is die keuze, één keer.
 */

/** De drie weergaven van het dieroverzicht: grote kaarten, compacte kaarten, lijst. */
export type Weergave = "kaart" | "compact" | "lijst";

export interface SoortTegel {
  /** De waarde die het filter zet; "" = alle soorten. */
  key: string;
  label: string;
  /** Het aantal in deze soort. Weglaten als het scherm geen aantallen kent. */
  aantal?: number;
  icon: ReactNode;
}

export function SoortTegels({ tegels, gekozen, onKies, label }: {
  tegels: SoortTegel[];
  gekozen: string;
  onKies: (key: string) => void;
  /** Toegankelijke naam van de groep, bv. "Soort product". */
  label: string;
}) {
  return (
    <div className="fawkes-catalogus__soorten" role="group" aria-label={label}>
      {tegels.map((tegel) => (
        <button
          key={tegel.key || "alle"}
          type="button"
          className={`fawkes-catalogus__soort${gekozen === tegel.key ? " is-active" : ""}`}
          aria-pressed={gekozen === tegel.key}
          onClick={() => onKies(tegel.key)}
        >
          <span className="fawkes-catalogus__soort-icon" aria-hidden="true">{tegel.icon}</span>
          <span className="fawkes-catalogus__soort-tekst">
            {tegel.aantal != null && (
              <span className="fawkes-catalogus__soort-aantal">{tegel.aantal.toLocaleString("nl-NL")}</span>
            )}
            <span className="fawkes-catalogus__soort-label">{tegel.label}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** De drie weergaveknoppen, met dezelfde tekens en volgorde als op het dieroverzicht. */
export function WeergaveKeuze({ weergave, onKies, labels }: {
  weergave: Weergave;
  onKies: (w: Weergave) => void;
  labels: { groep: string; kaart: string; compact: string; lijst: string };
}) {
  const knop = (w: Weergave, teken: string, naam: string) => (
    <button
      type="button"
      className={`fawkes-catalogus__weergaveknop${weergave === w ? " is-active" : ""}`}
      onClick={() => onKies(w)}
      title={naam}
      aria-label={naam}
      aria-pressed={weergave === w}
    >
      <span aria-hidden="true">{teken}</span>
    </button>
  );
  return (
    <div className="fawkes-catalogus__weergave" role="group" aria-label={labels.groep}>
      {knop("kaart", "▦", labels.kaart)}
      {knop("compact", "▥", labels.compact)}
      {knop("lijst", "☰", labels.lijst)}
    </div>
  );
}

/** Onthoudt de gekozen weergave per scherm, zodat je hem niet elke keer opnieuw instelt. */
export function leesWeergave(sleutel: string): Weergave {
  try {
    const v = localStorage.getItem(`${sleutel}.weergave`);
    return v === "kaart" || v === "compact" || v === "lijst" ? v : "lijst";
  } catch {
    return "lijst";
  }
}

export function bewaarWeergave(sleutel: string, weergave: Weergave) {
  try {
    localStorage.setItem(`${sleutel}.weergave`, weergave);
  } catch {
    // Een browser die opslag weigert hoort het scherm niet te breken.
  }
}
